import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import {
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ScaleIcon,
  ChatBubbleLeftRightIcon,
  DocumentMagnifyingGlassIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const CPDLegalConsentPage = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [readSections, setReadSections] = useState({});
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(null);

  // Calculate progress based on read sections
  useEffect(() => {
    const totalSections = 17;
    const readCount = Object.values(readSections).filter(Boolean).length;
    setProgress(Math.round((readCount / totalSections) * 100));
  }, [readSections]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
    
    // Mark as read when expanded
    if (!expandedSections[sectionId]) {
      setReadSections(prev => ({
        ...prev,
        [sectionId]: true
      }));
      setActiveSection(sectionId);
    }
  };

  // Course modules configuration
  const modules = [
    {
      id: 'module-1',
      title: '1. Welcome & Course Overview',
      icon: AcademicCapIcon,
      color: 'blue',
      content: (
        <div className="space-y-6">
          <blockquote className="text-xl italic text-gray-700 border-l-4 border-blue-500 pl-6 py-2">
            "Consent is more than a signature — it's the foundation of ethical dental care."
          </blockquote>
          
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h4 className="font-bold text-lg text-blue-900 mb-4 flex items-center gap-2">
              <CheckCircleIcon className="h-6 w-6" />
              Learning Outcomes
            </h4>
            <p className="text-gray-700 mb-4">After completing this course, you will be able to:</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Explain what makes consent valid under UK law.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Apply GDC and CQC requirements in daily practice.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Recognise when consent must be reviewed or renewed.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Improve how you communicate risk and options to patients.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Record and audit consent processes confidently.</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'module-2',
      title: '2. Why Consent Matters',
      icon: UserGroupIcon,
      color: 'indigo',
      content: (
        <div className="space-y-6">
          <p className="text-lg text-gray-700 leading-relaxed">
            Consent protects three groups simultaneously:
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h4 className="font-bold text-indigo-900 mb-3">Patients</h4>
              <p className="text-gray-700">By ensuring autonomy and informed decision-making.</p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h4 className="font-bold text-indigo-900 mb-3">Clinicians</h4>
              <p className="text-gray-700">By creating evidence of ethical practice.</p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h4 className="font-bold text-indigo-900 mb-3">The Profession</h4>
              <p className="text-gray-700">By maintaining public confidence.</p>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl">
            <div className="flex items-start gap-3">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-yellow-900 mb-2">Did you know?</h4>
                <p className="text-gray-700">
                  Over 20% of UK dental complaints mention inadequate or misunderstood consent.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-3',
      title: '3. The Legal Foundations',
      icon: ScaleIcon,
      color: 'purple',
      content: (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
              <thead>
                <tr className="bg-purple-600 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Framework</th>
                  <th className="px-6 py-4 text-left font-semibold">Key Requirement</th>
                  <th className="px-6 py-4 text-left font-semibold">What It Means for You</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-purple-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">Common Law</td>
                  <td className="px-6 py-4 text-gray-700">Consent must be voluntary, informed, and given by a person with capacity.</td>
                  <td className="px-6 py-4 text-gray-700">You must explain risks and alternatives clearly.</td>
                </tr>
                <tr className="hover:bg-purple-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">GDC Standards (2013)</td>
                  <td className="px-6 py-4 text-gray-700">Standard 3: Obtain valid consent before starting treatment.</td>
                  <td className="px-6 py-4 text-gray-700">Consent is an ongoing process, not a one-off form.</td>
                </tr>
                <tr className="hover:bg-purple-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">CQC Regulations</td>
                  <td className="px-6 py-4 text-gray-700">Practices must have systems for obtaining and recording consent.</td>
                  <td className="px-6 py-4 text-gray-700">Inspectors will check records and staff understanding.</td>
                </tr>
                <tr className="hover:bg-purple-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">Montgomery v Lanarkshire (2015)</td>
                  <td className="px-6 py-4 text-gray-700">Clinicians must disclose "material risks" relevant to the individual patient.</td>
                  <td className="px-6 py-4 text-gray-700">Shift from doctor-centred to patient-centred consent.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-xl">
            <h4 className="font-bold text-purple-900 mb-2">Key Definition</h4>
            <p className="text-gray-700">
              <strong>Material risk</strong> = any risk that a reasonable person in the patient's position would want to know, 
              or that this specific patient has shown concern about.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'module-4',
      title: '4. Understanding Valid Consent',
      icon: ClipboardDocumentCheckIcon,
      color: 'green',
      content: (
        <div className="space-y-6">
          <p className="text-lg text-gray-700 leading-relaxed">
            Valid consent requires three elements:
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-green-50 rounded-xl p-6 border-2 border-green-300">
              <div className="bg-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h4 className="font-bold text-green-900 mb-3 text-center">Voluntary</h4>
              <p className="text-gray-700 text-center">Free from pressure or coercion.</p>
            </div>
            <div className="bg-green-50 rounded-xl p-6 border-2 border-green-300">
              <div className="bg-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h4 className="font-bold text-green-900 mb-3 text-center">Informed</h4>
              <p className="text-gray-700 text-center">The patient understands the nature, purpose, risks, and alternatives.</p>
            </div>
            <div className="bg-green-50 rounded-xl p-6 border-2 border-green-300">
              <div className="bg-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h4 className="font-bold text-green-900 mb-3 text-center">Capacity</h4>
              <p className="text-gray-700 text-center">The patient can understand, retain, and weigh the information to decide.</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border-2 border-green-200 shadow-sm">
            <h4 className="font-bold text-lg text-green-900 mb-4">Types of Consent</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 text-green-800 font-bold px-3 py-1 rounded-lg text-sm">Implied</div>
                <p className="text-gray-700 flex-1">e.g., patient opens mouth for examination.</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-green-100 text-green-800 font-bold px-3 py-1 rounded-lg text-sm">Verbal</div>
                <p className="text-gray-700 flex-1">Agreement to a simple procedure.</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-green-100 text-green-800 font-bold px-3 py-1 rounded-lg text-sm">Written</div>
                <p className="text-gray-700 flex-1">For invasive or high-risk treatment, or when required by policy.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-5',
      title: '5. Communication and Comprehension',
      icon: ChatBubbleLeftRightIcon,
      color: 'blue',
      content: (
        <div className="space-y-6">
          <blockquote className="text-xl italic text-gray-700 border-l-4 border-blue-500 pl-6 py-2">
            "A patient's understanding is the real signature on your consent form."
          </blockquote>

          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h4 className="font-bold text-lg text-blue-900 mb-4">Practical Tips</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Use plain language; avoid jargon.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Illustrate with diagrams or models.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Check understanding by asking the patient to explain the plan back to you.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Provide written summaries for complex treatments.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Offer translation or interpretation if needed.</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl p-6 border-2 border-blue-300">
            <div className="flex items-center justify-between text-center">
              <div className="flex-1">
                <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
                  <p className="font-bold text-blue-900">Explain</p>
                </div>
              </div>
              <ArrowRightIcon className="h-6 w-6 text-blue-600 mx-2" />
              <div className="flex-1">
                <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
                  <p className="font-bold text-blue-900">Confirm</p>
                </div>
              </div>
              <ArrowRightIcon className="h-6 w-6 text-blue-600 mx-2" />
              <div className="flex-1">
                <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
                  <p className="font-bold text-blue-900">Document</p>
                </div>
              </div>
              <ArrowRightIcon className="h-6 w-6 text-blue-600 mx-2" />
              <div className="flex-1">
                <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
                  <p className="font-bold text-blue-900">Review</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-6',
      title: '6. Assessing Capacity',
      icon: DocumentMagnifyingGlassIcon,
      color: 'indigo',
      content: (
        <div className="space-y-6">
          <div className="bg-indigo-600 text-white rounded-xl p-6">
            <h4 className="font-bold text-xl mb-4">The Mental Capacity Act 2005</h4>
            <p className="text-indigo-100">Five guiding principles:</p>
          </div>

          <div className="space-y-4">
            {[
              'Assume capacity unless proven otherwise.',
              'Provide support to help decision-making.',
              'People can make unwise decisions.',
              'Any act on behalf of someone lacking capacity must be in their best interests.',
              'Choose the least restrictive option possible.'
            ].map((principle, index) => (
              <div key={index} className="flex items-start gap-4 bg-white rounded-lg p-4 border border-indigo-200 shadow-sm">
                <div className="bg-indigo-100 text-indigo-800 font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-gray-700 flex-1">{principle}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h4 className="font-bold text-indigo-900 mb-3">Under 16s</h4>
              <p className="text-gray-700">
                Assess <strong>Gillick competence</strong> — can the child understand the nature and consequences?
              </p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h4 className="font-bold text-indigo-900 mb-3">Ages 16–17</h4>
              <p className="text-gray-700">
                Presumed to have capacity unless evidence shows otherwise.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-7',
      title: '7. The Consent Process Step-by-Step',
      icon: DocumentTextIcon,
      color: 'green',
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            {[
              { step: 'Discuss', desc: 'Explain diagnosis, options, and likely outcomes.' },
              { step: 'Disclose', desc: 'Outline benefits, risks (common + material), and costs.' },
              { step: 'Decide', desc: "Confirm patient's questions have been answered." },
              { step: 'Document', desc: 'Record what was discussed, not just the decision.' },
              { step: 'Review', desc: 'Reconfirm consent when treatment or circumstances change.' }
            ].map((item, index) => (
              <div key={index} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-l-4 border-green-500 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="bg-green-600 text-white font-bold rounded-lg px-4 py-2 text-lg">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-green-900 mb-2 text-lg">{item.step}</h4>
                    <p className="text-gray-700">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl">
            <div className="flex items-start gap-3">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-yellow-900 mb-2">Pro-tip</h4>
                <p className="text-gray-700">
                  Always document what was said, what the patient asked, and what they decided.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-8',
      title: '8. Documentation and Record-Keeping',
      icon: DocumentTextIcon,
      color: 'purple',
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 rounded-xl p-6 border-2 border-green-400">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                <h4 className="font-bold text-green-900">Good Record</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>"Explained risks of sensitivity and alternative crowns. Patient chose composite."</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Includes date, time, and clinician initials</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Stored securely, GDPR-compliant</span>
                </li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-xl p-6 border-2 border-red-400">
              <div className="flex items-center gap-2 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                <h4 className="font-bold text-red-900">Poor Record</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>"Patient consented to treatment."</span>
                </li>
                <li className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>No context or reasoning</span>
                </li>
                <li className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Paper lost or incomplete</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-purple-600 text-white rounded-xl p-6">
            <h4 className="font-bold text-xl mb-4">Digital Documentation Advantages</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Time-stamped, secure, legible.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Easy to retrieve for audits or CQC inspection.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>Integrates with practice management systems.</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
            <p className="text-center text-gray-700 mb-4">
              <strong>Smile Consent</strong> provides secure, GDPR-compliant digital consent management.
            </p>
            <div className="text-center">
              <Link 
                to="/dental-compliance-software"
                className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Learn more about digital compliance
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-9',
      title: '9. Common Pitfalls and How to Avoid Them',
      icon: ExclamationTriangleIcon,
      color: 'red',
      content: (
        <div className="space-y-4">
          {[
            { 
              pitfall: 'Relying on generic forms',
              solution: 'Customise for each patient.'
            },
            { 
              pitfall: 'Assuming understanding',
              solution: 'Ask the patient to summarise the plan.'
            },
            { 
              pitfall: 'Skipping discussion of alternatives',
              solution: 'Always note why options were declined.'
            },
            { 
              pitfall: 'Failing to revisit consent after changes',
              solution: 'Re-confirm before continuing.'
            },
            { 
              pitfall: 'Poor documentation',
              solution: 'Treat consent notes as part of clinical records.'
            }
          ].map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-6 border-l-4 border-red-500 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="bg-red-100 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-red-900 mb-2">Pitfall: {item.pitfall}</h4>
                  <p className="text-gray-700">
                    <span className="text-green-700 font-semibold">→ Solution:</span> {item.solution}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      id: 'module-10',
      title: '10. Complex Scenarios',
      icon: ShieldCheckIcon,
      color: 'blue',
      content: (
        <div className="space-y-6">
          {[
            {
              title: 'A. Emergency Treatment',
              content: 'If delaying treatment risks serious harm and the patient can\'t consent → act in best interests and record rationale.',
              color: 'red'
            },
            {
              title: 'B. Language Barriers',
              content: 'Use professional interpreters; never rely solely on family members. Document who translated.',
              color: 'blue'
            },
            {
              title: 'C. Cosmetic Dentistry',
              content: 'Check motivation; ensure expectations are realistic; avoid emotional pressure.',
              color: 'purple'
            },
            {
              title: 'D. Remote or Digital Consent',
              content: 'Digital forms are acceptable if the discussion standard is identical and verification secure.',
              color: 'green'
            },
            {
              title: 'E. Refusal of Treatment',
              content: 'Respect the decision, explain potential consequences, and document clearly.',
              color: 'orange'
            }
          ].map((scenario, index) => (
            <div key={index} className={`bg-${scenario.color}-50 rounded-xl p-6 border border-${scenario.color}-200`}>
              <h4 className={`font-bold text-${scenario.color}-900 mb-3 text-lg`}>{scenario.title}</h4>
              <p className="text-gray-700">{scenario.content}</p>
            </div>
          ))}
        </div>
      )
    },
    {
      id: 'module-11',
      title: '11. Ethical Challenges',
      icon: ScaleIcon,
      color: 'indigo',
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 border-2 border-indigo-200 shadow-sm">
              <div className="bg-indigo-600 text-white rounded-lg px-4 py-2 mb-4 text-center font-bold">
                Scenario 1
              </div>
              <h4 className="font-bold text-indigo-900 mb-3">Time Pressure</h4>
              <p className="text-gray-600 mb-4 italic">A late patient wants a quick signature.</p>
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong className="text-indigo-900">Decision:</strong> Reschedule rather than rush.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-indigo-200 shadow-sm">
              <div className="bg-indigo-600 text-white rounded-lg px-4 py-2 mb-4 text-center font-bold">
                Scenario 2
              </div>
              <h4 className="font-bold text-indigo-900 mb-3">Nervous Patient</h4>
              <p className="text-gray-600 mb-4 italic">Patient is very anxious about treatment.</p>
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong className="text-indigo-900">Decision:</strong> Provide information gradually; use sedation discussions carefully.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-indigo-200 shadow-sm">
              <div className="bg-indigo-600 text-white rounded-lg px-4 py-2 mb-4 text-center font-bold">
                Scenario 3
              </div>
              <h4 className="font-bold text-indigo-900 mb-3">Conflicting Wishes</h4>
              <p className="text-gray-600 mb-4 italic">Parents disagree about child treatment.</p>
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong className="text-indigo-900">Decision:</strong> Focus on best interests and seek advice if needed.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-12',
      title: '12. Linking Consent to Quality & Compliance',
      icon: ClipboardDocumentCheckIcon,
      color: 'green',
      content: (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
              <thead>
                <tr className="bg-green-600 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Aspect</th>
                  <th className="px-6 py-4 text-left font-semibold">Why It Matters</th>
                  <th className="px-6 py-4 text-left font-semibold">Inspection Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-green-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">Staff Training</td>
                  <td className="px-6 py-4 text-gray-700">Demonstrates understanding of patient rights.</td>
                  <td className="px-6 py-4 text-gray-700">Training logs, CPD records.</td>
                </tr>
                <tr className="hover:bg-green-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">Audit of Consent Forms</td>
                  <td className="px-6 py-4 text-gray-700">Ensures consistency and completeness.</td>
                  <td className="px-6 py-4 text-gray-700">Periodic sample of records.</td>
                </tr>
                <tr className="hover:bg-green-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">Complaints Analysis</td>
                  <td className="px-6 py-4 text-gray-700">Identifies communication issues.</td>
                  <td className="px-6 py-4 text-gray-700">Review outcomes & improvements.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
            <p className="text-center text-gray-700 mb-4">
              Need help preparing for CQC inspections?
            </p>
            <div className="text-center">
              <Link 
                to="/dental-compliance-software"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                View compliance solutions
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-13',
      title: '13. Digital Transformation in Consent',
      icon: SparklesIcon,
      color: 'purple',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-xl p-8">
            <h4 className="font-bold text-2xl mb-6 text-center">The Smile Consent Approach</h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <CheckCircleIcon className="h-8 w-8 mb-3" />
                <p className="font-semibold mb-2">Pre-filled Templates</p>
                <p className="text-purple-100 text-sm">Editable for each patient's needs</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <CheckCircleIcon className="h-8 w-8 mb-3" />
                <p className="font-semibold mb-2">Secure Cloud Storage</p>
                <p className="text-purple-100 text-sm">GDPR-compliant and accessible</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <CheckCircleIcon className="h-8 w-8 mb-3" />
                <p className="font-semibold mb-2">Digital Signatures</p>
                <p className="text-purple-100 text-sm">With complete audit trails</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <CheckCircleIcon className="h-8 w-8 mb-3" />
                <p className="font-semibold mb-2">Automatic Reminders</p>
                <p className="text-purple-100 text-sm">For re-consent when needed</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link 
              to="/dental-consent-plans"
              className="inline-flex items-center gap-2 bg-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Explore Smile Consent Plans
              <ArrowRightIcon className="h-6 w-6" />
            </Link>
          </div>
        </div>
      )
    },
    {
      id: 'module-14',
      title: '14. Reflection & Self-Assessment',
      icon: LightBulbIcon,
      color: 'yellow',
      content: (
        <div className="space-y-6">
          <blockquote className="text-xl italic text-gray-700 border-l-4 border-yellow-500 pl-6 py-2">
            "What one change will you make tomorrow to strengthen your consent process?"
          </blockquote>

          <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
            <h4 className="font-bold text-lg text-yellow-900 mb-4">Prompts for Reflection</h4>
            <p className="text-gray-600 mb-4 text-sm">
              Use these text areas for your personal notes during the course. You'll be asked to share your reflections in the final feedback form.
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-gray-700 font-medium mb-2">How do I check understanding before treatment?</p>
                <textarea 
                  className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows="3"
                  placeholder="Write your personal reflection here..."
                ></textarea>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-gray-700 font-medium mb-2">Do I record both risks and patient questions?</p>
                <textarea 
                  className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows="3"
                  placeholder="Write your personal reflection here..."
                ></textarea>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-gray-700 font-medium mb-2">When was the last time I audited consent forms?</p>
                <textarea 
                  className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows="3"
                  placeholder="Write your personal reflection here..."
                ></textarea>
              </div>
            </div>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> These reflections are for your personal use. You'll copy and paste them into the final feedback form to complete your CPD certificate.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl p-6 border-2 border-yellow-400">
            <p className="text-center text-gray-800 font-semibold text-lg">
              📝 Set a personal goal for improvement this month
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'module-15',
      title: '15. Summary & Key Take-Aways',
      icon: CheckCircleIcon,
      color: 'green',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border-2 border-green-300">
            <h4 className="font-bold text-2xl text-green-900 mb-6 text-center">Key Take-Aways</h4>
            <div className="space-y-4">
              {[
                'Consent is communication, not paperwork.',
                'It must be voluntary, informed, and by a competent patient.',
                'Record discussions, not just signatures.',
                'Review consent whenever circumstances change.',
                'Digital systems can enhance accuracy, accessibility, and compliance.'
              ].map((takeaway, index) => (
                <div key={index} className="flex items-start gap-4 bg-white rounded-lg p-4 shadow-sm">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-lg">{takeaway}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'module-16',
      title: '16. Further Reading & Resources',
      icon: DocumentTextIcon,
      color: 'blue',
      content: (
        <div className="space-y-4">
          {[
            {
              title: 'GDC Standards for the Dental Team (2013)',
              desc: 'Standard 3: Obtaining valid consent',
              link: 'https://www.gdc-uk.org/professionals/standards'
            },
            {
              title: 'CQC Guidance: Regulation 11',
              desc: 'Need for consent',
              link: 'https://www.cqc.org.uk/guidance-providers/regulations-enforcement/regulation-11-need-consent'
            },
            {
              title: 'Mental Capacity Act 2005',
              desc: 'Code of Practice',
              link: 'https://www.gov.uk/government/publications/mental-capacity-act-code-of-practice'
            },
            {
              title: 'Montgomery v Lanarkshire Health Board (2015)',
              desc: 'UK Supreme Court ruling on material risk',
              link: 'https://www.supremecourt.uk/cases/uksc-2013-0136.html'
            },
            {
              title: 'NHS Consent Policy',
              desc: 'Latest edition',
              link: 'https://www.england.nhs.uk/publication/consent-policy/'
            }
          ].map((resource, index) => (
            <a
              key={index}
              href={resource.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-xl p-6 border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-bold text-blue-900 mb-2">{resource.title}</h4>
                  <p className="text-gray-600">{resource.desc}</p>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
              </div>
            </a>
          ))}
        </div>
      )
    },
    {
      id: 'module-17',
      title: '17. Next Steps – CPD Verification',
      icon: AcademicCapIcon,
      color: 'purple',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-xl p-8 text-center">
            <AcademicCapIcon className="h-16 w-16 mx-auto mb-4" />
            <h4 className="font-bold text-3xl mb-4">Complete Your CPD</h4>
            <p className="text-purple-100 text-lg mb-6">
              To receive your verifiable CPD certificate, complete the reflection and feedback form.
            </p>
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-left">
                <CheckCircleIcon className="h-6 w-6 mb-2" />
                <p className="font-semibold">Review your notes and reflections from this course</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-left">
                <CheckCircleIcon className="h-6 w-6 mb-2" />
                <p className="font-semibold">Complete the external feedback form (opens in new tab)</p>
                <p className="text-purple-100 text-sm mt-1">Copy your reflections and provide course feedback</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-left">
                <CheckCircleIcon className="h-6 w-6 mb-2" />
                <p className="font-semibold">Receive your CPD certificate via email</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://feedback.enamelacademy.co.uk/HwGQXM"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-10 py-5 rounded-xl text-xl font-bold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <DocumentTextIcon className="h-8 w-8" />
              Complete the CPD Reflection & Feedback
              <ArrowRightIcon className="h-6 w-6" />
            </a>
            <p className="text-gray-600 mt-4 text-sm">Opens in a new tab • Takes 5-10 minutes</p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl">
            <div className="flex items-start gap-3">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-yellow-900 mb-2">Certificate Information</h4>
                <p className="text-gray-700">
                  Your CPD certificate will be automatically issued after completing the feedback form. 
                  It includes a unique verification code for your professional records.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Legal Consent in Dentistry — Free 1 Hour CPD | Smile Consent</title>
        <meta 
          name="description" 
          content="Free 1-hour verifiable CPD for dental professionals on legal consent. Covers GDC, CQC, and UK legal frameworks. Certificate on completion." 
        />
        <meta 
          name="keywords" 
          content="legal consent dentistry uk, dental consent CPD, GDC compliance CPD, Smile Consent course, verifiable CPD dentistry, dental consent training, UK dental law" 
        />
        <link rel="canonical" href="https://smileconsent.co.uk/cpd-legal-consent" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Legal Consent in Dentistry — Free 1 Hour CPD | Smile Consent" />
        <meta property="og:description" content="Free 1-hour verifiable CPD for dental professionals on legal consent. Covers GDC, CQC, and UK legal frameworks." />
        <meta property="og:url" content="https://smileconsent.co.uk/cpd-legal-consent" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image-cpd.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Legal Consent in Dentistry — Free 1 Hour CPD" />
        <meta name="twitter:description" content="Free verifiable CPD for UK dental professionals covering GDC and CQC consent topics." />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image-cpd.png" />
        
        {/* JSON-LD Structured Data for Course */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "Legal Consent in Dentistry",
            "description": "A 1-hour verifiable CPD course for UK dental professionals covering legal and ethical consent, GDC and CQC standards, and practical documentation methods.",
            "provider": {
              "@type": "Organization",
              "name": "Smile Consent",
              "url": "https://smileconsent.co.uk",
              "logo": "https://smileconsent.co.uk/logo.png"
            },
            "educationalCredentialAwarded": "1 Hour Verifiable CPD",
            "timeRequired": "PT1H",
            "inLanguage": "en-GB",
            "learningResourceType": "Professional development",
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": "Dental Professional"
            },
            "teaches": [
              "Understanding valid consent under UK law",
              "GDC and CQC compliance requirements",
              "Consent documentation and record-keeping",
              "Communication and patient comprehension",
              "Capacity assessment"
            ],
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "online",
              "courseWorkload": "PT1H",
              "datePublished": "2025"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <AcademicCapIcon className="h-5 w-5" />
              <span className="text-sm font-semibold">FREE CPD COURSE</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              🦷 Legal Consent in Dentistry
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-4">
              Free 1-Hour Verifiable CPD Course
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm md:text-base">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                <ShieldCheckIcon className="h-5 w-5" />
                <span>GDC Outcomes: A, B, C, D</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                <ClockIcon className="h-5 w-5" />
                <span>1 Hour Reading Time</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                <AcademicCapIcon className="h-5 w-5" />
                <span>Certificate Included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Progress Bar - Sticky */}
      <div className="sticky top-0 z-40 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Course Progress</span>
            <span className="text-sm font-semibold text-blue-600">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-600 to-purple-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation - Sticky */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <h3 className="font-bold text-lg mb-4 text-gray-900">Course Modules</h3>
                <nav className="space-y-2">
                  {modules.map((module, index) => {
                    const Icon = module.icon;
                    const isRead = readSections[module.id];
                    const isActive = activeSection === module.id;
                    return (
                      <button
                        key={module.id}
                        onClick={() => {
                          toggleSection(module.id);
                          document.getElementById(module.id)?.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isActive 
                            ? 'bg-blue-100 text-blue-900 font-semibold' 
                            : isRead
                            ? 'bg-green-50 text-green-900'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm flex-1">{index + 1}. {module.title.split('. ')[1]}</span>
                        {isRead && <CheckCircleIcon className="h-4 w-4 text-green-600" />}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          {/* Course Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Introduction Box */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border-2 border-blue-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to the Course</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                This comprehensive CPD course covers the legal and ethical foundations of consent in UK dental practice. 
                You'll learn about GDC standards, CQC requirements, and practical strategies for obtaining, documenting, 
                and auditing consent processes.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                The course is designed to be completed in approximately 60 minutes. You can work through it at your own 
                pace, and your progress will be tracked automatically.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/dental-compliance-software"
                  className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-2"
                >
                  Learn about CQC compliance tools
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <Link 
                  to="/dental-consent-plans"
                  className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-2"
                >
                  View pricing plans
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Module Sections */}
            {modules.map((module) => {
              const Icon = module.icon;
              const isExpanded = expandedSections[module.id];
              return (
                <div 
                  key={module.id}
                  id={module.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 scroll-mt-32"
                >
                  <button
                    onClick={() => toggleSection(module.id)}
                    className={`w-full flex items-center justify-between p-6 text-left transition-colors ${
                      isExpanded ? `bg-${module.color}-50` : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`bg-${module.color}-600 text-white rounded-lg p-3`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{module.title}</h3>
                    </div>
                    {isExpanded ? (
                      <ChevronUpIcon className="h-6 w-6 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 text-gray-500" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="p-6 border-t border-gray-200">
                      {module.content}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Final CTA */}
            <div className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-xl p-8 text-center shadow-xl">
              <AcademicCapIcon className="h-16 w-16 mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-4">Ready to Complete Your CPD?</h3>
              <p className="text-purple-100 text-lg mb-6">
                Complete the reflection form to receive your verifiable CPD certificate
              </p>
              <a
                href="https://feedback.enamelacademy.co.uk/HwGQXM"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-white text-purple-600 px-8 py-4 rounded-xl text-lg font-bold hover:bg-purple-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <DocumentTextIcon className="h-6 w-6" />
                Complete CPD Reflection & Feedback
                <ArrowRightIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Smile Consent</h3>
              <p className="text-gray-400 text-sm">
                Digital consent solution for dental practices — AI-assisted drafting with clinician review; aligned with common GDC and CQC documentation themes.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Consent Forms</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><Link to="/cpd-legal-consent" className="text-gray-400 hover:text-white text-sm">CPD Course</Link></li>
                <li><Link to="/dentaltriage" className="text-gray-400 hover:text-white text-sm">Free Triage Tool</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
            <CompanyDisclosure tone="dark" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CPDLegalConsentPage;

